"use client"

import type React from "react"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Mail, CheckCircle } from "lucide-react"

interface NewsletterFormProps {
  className?: string
  title?: string
  description?: string
  buttonText?: string
  compact?: boolean
}

export default function NewsletterForm({
  className = "",
  title = "Subscribe to Our Newsletter",
  description = "Get the latest travel tips, destinations, and exclusive content delivered to your inbox.",
  buttonText = "Subscribe",
  compact = false,
}: NewsletterFormProps) {
  const [email, setEmail] = useState("")
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle")
  const [errorMessage, setErrorMessage] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      setStatus("error")
      setErrorMessage("Please enter your email address")
      return
    }

    setStatus("loading")

    // Simulate API call
    setTimeout(() => {
      // In a real app, you would call your API here
      // For demo purposes, we'll just simulate success
      setStatus("success")
      setEmail("")
    }, 1000)
  }

  return (
    <div className={`bg-card rounded-2xl p-6 md:p-8 shadow-sm ${className}`}>
      <div className="max-w-2xl mx-auto text-center">
        {!compact && (
          <>
            <Mail className="h-8 w-8 text-primary mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold mb-3">{title}</h2>
            <p className="text-muted-foreground mb-6">{description}</p>
          </>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <div className="relative flex-1">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === "loading" || status === "success"}
              className={`px-4 py-3 h-auto ${status === "error" ? "border-destructive focus:ring-destructive" : ""}`}
              aria-label="Email address"
              aria-invalid={status === "error"}
              aria-describedby={status === "error" ? "newsletter-error" : undefined}
            />
            {status === "error" && (
              <div id="newsletter-error" className="text-xs text-destructive mt-1 text-left">
                {errorMessage}
              </div>
            )}
          </div>

          <Button
            type="submit"
            className="whitespace-nowrap h-auto py-3"
            disabled={status === "loading" || status === "success"}
          >
            {status === "loading" ? (
              <>
                <span className="animate-spin mr-2">⟳</span>
                Subscribing...
              </>
            ) : status === "success" ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Subscribed!
              </>
            ) : (
              buttonText
            )}
          </Button>
        </form>

        {status === "success" && (
          <p className="text-sm text-primary mt-4 flex items-center justify-center">
            <CheckCircle className="h-4 w-4 mr-2" />
            Thank you for subscribing! Check your inbox to confirm your subscription.
          </p>
        )}

        <p className="text-xs text-muted-foreground mt-4">We respect your privacy. Unsubscribe at any time.</p>
      </div>
    </div>
  )
}
